import { useState, useEffect, useContext } from "react";
import {
  Grid,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Box,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import OrgSelect from "../../components/OrgSelect";
import InvoiceList from "./components/InvoiceList";
import PaymentDialog from "../InvoiceDetail/components/PaymentDialog";
import ManualInvoice from "../../components/ManualInvoice";
import AutoPayConsentDialog from "../InvoiceDetail/components/AutoPayConsentDialog";
import AutoPayInfoDialog from "../InvoiceDetail/components/AutoPayInfoDialog";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import IconButton from "@mui/material/IconButton";
import { EventContext } from "../../contexts/EventContext";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";

const Invoices = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { orgId } = useParams();
  const { userData } = useContext(EventContext);
  const [query, setQuery] = useState({ status: "open" });
  const [org, setOrg] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [totalSelected, setTotalSelected] = useState(0);
  const [statusFilter, setStatusFilter] = useState("open");
  const [loading, setLoading] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [showBtns, setShowBtns] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);

  const [autoPayStatus, setAutoPayStatus] = useState(null);
  const [autoPayConsentOpen, setAutoPayConsentOpen] = useState(false);
  const [autoPayInfoOpen, setAutoPayInfoOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (Object.keys(query).length > 0) {
      getData(0, false);
      setSelected([]);
    }
    if (org !== orgId) {
      setOrg(orgId);
    }
  }, [query, orgId]);

  useEffect(() => {
    if (selected.length > 0 && ["open", "overdue"].includes(statusFilter)) {
      const total = invoices
        .filter((i) => selected.includes(i._id))
        .reduce((sum, item) => sum + item.amountDue, 0);
      // const total = selected.reduce((sum, item) => sum + item.amountDue, 0);
      setTotalSelected(total);
      setShowBtns(true);
    } else {
      setShowBtns(false);
      setTotalSelected(0);
    }
  }, [selected]);

  useEffect(() => {
    const pi = searchParams.get("payment_intent");
    if (pi && !paymentIntentId) {
      setPaymentIntentId(pi);
      getData(0, false);
      //TODO: Show success here
    }
  }, [searchParams]);

  useEffect(() => {
    fetchAutoPayStatus();
  }, []);

  const fetchAutoPayStatus = async () => {
    const result = await request("contracts/autopay/status");
    if (result.success) setAutoPayStatus(result.data);
  };

  const handleAutoPayToggle = (e) => {
    if (e.target.checked) {
      setAutoPayConsentOpen(true);
    } else {
      submitAutoPayToggle(false);
    }
  };

  const submitAutoPayToggle = async (enable, authorized = false) => {
    const result = await request("contracts/autopay/toggle", "post", { enable, authorized });
    if (result.success) {
      setAutoPayStatus(result.data);
      toast.success(enable ? "Auto-pay enabled" : "Auto-pay disabled");
    } else {
      toast.error(result.error);
    }
  };

  const getData = async (pageNum = 0, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    let url = `contracts/invoice?page=${pageNum}&`;
    Object.entries(query).forEach(([key, value]) => {
      url += `${encodeURIComponent(key)}=${encodeURIComponent(value)}&`;
    });
    if (orgId) {
      url += `orgId=${orgId}`;
    }

    const result = await request(url);

    if (result.success) {
      if (append) {
        setInvoices((prev) => [...prev, ...result.invoices]);
        setPage(pageNum);
      } else {
        setInvoices(result.invoices);
        setPage(0);
      }

      setHasMore(result.pagination?.hasMore);
    }

    setLoading(false);
    setLoadingMore(false);
  };

  const handleOrgChange = (orgId) => {
    setOrg(orgId);
    navigate(`/invoices/${orgId}`);
  };

  const handleInvoiceSelect = (inv) => {
    navigate(`/invoice/${inv._id}`);
  };

  const runBatch = async () => {
    setBatchRunning(true);
    const result = await request("contracts/invoice/batch", "post", {});
    if (result.success) {
      toast.success("Batch run complete");
    }
    setBatchRunning(false);
  };

  return (
    <Grid container style={{ padding: 20 }} spacing={3} justifyContent="center">
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={2}>
            <Button size="small" onClick={() => navigate("/admin")}>
              back to admin
            </Button>
          </Grid>
          <Grid item xs={8}>
            {" "}
            <Typography variant="h5" align="center">
              Invoices
            </Typography>
          </Grid>
          <Grid item xs={2}></Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        {userData?.role === "superadmin" ? (
          <OrgSelect org={org} setOrg={handleOrgChange} />
        ) : null}
      </Grid>

      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(e, val) => {
            if (val !== null) {
              setStatusFilter(val);
              setQuery((prev) => ({ ...prev, status: val }));
            }
          }}
          aria-label="invoice status filter"
          size="small"
        >
          <ToggleButton value="open" aria-label="open invoices">
            Open
          </ToggleButton>
          <ToggleButton value="overdue" aria-label="overdue invoices">
            Overdue
          </ToggleButton>
          <ToggleButton value="disputed" aria-label="disputed invoices">
            Disputed
          </ToggleButton>
          <ToggleButton value="paid" aria-label="paid invoices">
            Paid
          </ToggleButton>
          <ToggleButton value="void" aria-label="canceled invoices">
            Canceled
          </ToggleButton>
        </ToggleButtonGroup>
        {userData?.role === "superadmin" ? (
          <Button
            onClick={() => setManualOpen(true)}
            variant="outlined"
            size="small"
            style={{ height: 30 }}
          >
            Create Invoice
          </Button>
        ) : null}
        {userData?.role === "superadmin" ? (
          <Button onClick={runBatch}>
            {batchRunning ? <CircularProgress size={20} /> : "Run Batch"}
          </Button>
        ) : null}
        {autoPayStatus !== null && userData?.role !== "superadmin" && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoPayStatus.autoPay}
                  onChange={handleAutoPayToggle}
                  disabled={!autoPayStatus.hasPaymentMethod}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  {autoPayStatus.autoPay ? "Auto-pay on" : "Auto-pay off"}
                </Typography>
              }
            />
            <IconButton size="small" onClick={() => setAutoPayInfoOpen(true)}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </div>
        )}
      </Grid>
      {showBtns ? (
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            alignItems: "center",
          }}
        >
          <Typography variant="button">Pay Now with:</Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ flex: 1 }}></div>
            <div style={{ display: "flex", gap: 10 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setPaymentMethod("ach");
                  setDialogOpen(true);
                }}
              >
                ACH
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setPaymentMethod("card");
                  setDialogOpen(true);
                }}
              >
                Credit Card
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setPaymentMethod("zelle");
                  setDialogOpen(true);
                }}
              >
                Zelle
              </Button>
            </div>
            <div
              style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
            >
              <Typography style={{ paddingRight: 10 }}>
                Total: ${totalSelected}
              </Typography>
            </div>
          </div>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : invoices.length === 0 ? (
          <Typography align="center" sx={{ mt: 4 }}>
            No invoices to display
          </Typography>
        ) : (
          <InvoiceList
            invoices={invoices}
            setInvoice={handleInvoiceSelect}
            status={statusFilter}
            selected={selected}
            setSelected={setSelected}
            onLoadMore={() => getData(page + 1, true)}
            hasMore={hasMore}
            loadingMore={loadingMore}
          />
        )}
      </Grid>
      <PaymentDialog
        open={dialogOpen}
        close={() => setDialogOpen(false)}
        invoiceId={selected}
        method={paymentMethod}
      />
      <ManualInvoice
        open={manualOpen}
        close={() => {
          setManualOpen(false);
          getData(0, false);
        }}
        orgId={orgId}
      />
      <AutoPayConsentDialog
        open={autoPayConsentOpen}
        paymentMethodType={autoPayStatus?.defaultPaymentMethodType}
        onAgree={() => {
          setAutoPayConsentOpen(false);
          submitAutoPayToggle(true, true);
        }}
        onCancel={() => setAutoPayConsentOpen(false)}
      />
      <AutoPayInfoDialog
        open={autoPayInfoOpen}
        close={() => setAutoPayInfoOpen(false)}
      />
    </Grid>
  );
};

export default Invoices;
